import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient, MaybeMasked } from '@apollo/client';
import {
  ControlSliceQuery,
  GET_COMMERCIAL_CONTROL_DATA,
  GET_ENGINEERING_CONTROL_DATA,
  GET_PROJECT_MANAGEMENT_CONTROL_DATA,
} from './graphql/controlQueries';
import { CommercialControlSlice, EngineeringControlSlice, ProjectManagementControlSlice } from './types';

type ControlSliceRefetch<TSlice> = () => Promise<ApolloClient.QueryResult<MaybeMasked<{ baseProject: TSlice }>>>;

export interface ControlSlices {
  refetchCommercialData: ControlSliceRefetch<CommercialControlSlice>;
  refetchProjectManagementData: ControlSliceRefetch<ProjectManagementControlSlice>;
  refetchEngineeringData: ControlSliceRefetch<EngineeringControlSlice>;
}

export const useControlSlices = (projectId: string | number): ControlSlices => {
  const client = useApolloClient();

  const refetchSlice = useCallback(
    <TSlice>(query: ControlSliceQuery<TSlice>) =>
      client.query({
        query,
        variables: { id: projectId?.toString() },
        fetchPolicy: 'network-only',
      }),
    [client, projectId],
  );

  const refetchCommercialData = useCallback(() => refetchSlice(GET_COMMERCIAL_CONTROL_DATA), [refetchSlice]);

  const refetchProjectManagementData = useCallback(
    () => refetchSlice(GET_PROJECT_MANAGEMENT_CONTROL_DATA),
    [refetchSlice],
  );

  const refetchEngineeringData = useCallback(() => refetchSlice(GET_ENGINEERING_CONTROL_DATA), [refetchSlice]);

  return { refetchCommercialData, refetchProjectManagementData, refetchEngineeringData };
};
